import { Component } from "@theme/component";
import { sectionRenderer } from "@theme/section-renderer";
import { requestIdleCallback, viewTransition } from "@theme/utilities";
import { ThemeEvents } from "@theme/events";
import { PaginatedListAspectRatioHelper } from "@theme/paginated-list-aspect-ratio";

/**
 * A custom element that renders a paginated list of items.
 *
 * @typedef {object} Refs
 * @property {HTMLUListElement} [grid] - The grid element.
 * @property {HTMLSpanElement} [viewMorePrevious] - The view more previous button.
 * @property {HTMLSpanElement} [viewMoreNext] - The view more next button.
 * @property {HTMLButtonElement} [loadMoreButton] - Load more control (button trigger mode).
 * @property {HTMLElement} [loadMoreFooter] - Wrapper for load more control.
 * @property {HTMLElement} [loadMoreStatus] - “Showing X–Y of Z” line (button trigger mode).
 * @property {HTMLButtonElement} [loadPreviousButton] - Load one earlier page (deep-linked page > 1).
 * @property {HTMLElement} [loadPreviousFooter] - Wrapper for load previous control.
 * @property {HTMLElement[]} [cards] - The cards elements.
 *
 * @extends Component<Refs>
 */
export default class PaginatedList extends Component {
  /**
   * @type {Map<number, string>}
   */
  pages = new Map();

  /** @type {IntersectionObserver | undefined} */
  infinityScrollObserver;

  /** @type {((value: void) => void) | null} */
  #resolveNextPagePromise = null;

  /** @type {((value: void) => void) | null} */
  #resolvePreviousPagePromise = null;

  /** @type {PaginatedListAspectRatioHelper} */
  #aspectRatioHelper;

  /** @type {boolean} */
  #loadMoreInFlight = false;

  /** @type {boolean} */
  #renderPreviousInFlight = false;

  /** @returns {boolean} */
  #appendPaginationEnabled() {
    return this.getAttribute("infinite-scroll") === "true";
  }

  /** @returns {string} */
  #infiniteScrollTrigger() {
    return this.getAttribute("infinite-scroll-trigger") || "scroll";
  }

  /** @returns {boolean} */
  #isScrollTrigger() {
    return (
      this.#appendPaginationEnabled() &&
      this.#infiniteScrollTrigger() === "scroll"
    );
  }

  /** @returns {boolean} */
  #isButtonTrigger() {
    return (
      this.#appendPaginationEnabled() &&
      this.#infiniteScrollTrigger() === "button"
    );
  }

  connectedCallback() {
    super.connectedCallback();

    /** @type {HTMLElement | null} */
    const templateCard = this.querySelector('[ref="cardGallery"]');
    if (templateCard) {
      this.#aspectRatioHelper = new PaginatedListAspectRatioHelper({
        templateCard
      });
    }

    if (this.#appendPaginationEnabled()) {
      this.#fetchPage("next");
      if (this.#isScrollTrigger()) {
        this.#fetchPage("previous");
      }
      this.#observeViewMore();
      queueMicrotask(() => {
        this.#syncLoadPreviousButton();
        if (this.#isButtonTrigger()) {
          this.#syncLoadMoreButton();
        }
      });
    }

    // Listen for filter updates to clear cached pages
    document.addEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    if (this.infinityScrollObserver) {
      this.infinityScrollObserver.disconnect();
    }
    // Remove the filter update listener
    document.removeEventListener(
      ThemeEvents.FilterUpdate,
      this.#handleFilterUpdate
    );
  }

  #observeViewMore() {
    const { viewMorePrevious, viewMoreNext } = this.refs;

    // Return if neither element exists
    if (!viewMorePrevious && !viewMoreNext) return;

    // Create observer if it doesn't exist
    if (!this.infinityScrollObserver) {
      this.infinityScrollObserver = new IntersectionObserver(
        async (entries) => {
          // Wait for any in-progress view transitions to finish
          if (viewTransition.current) await viewTransition.current;

          for (const entry of entries) {
            if (entry.isIntersecting) {
              // Use current refs to check which element triggered
              const { viewMorePrevious, viewMoreNext } = this.refs;

              if (entry.target === viewMorePrevious) {
                this.#renderPreviousPage();
              } else if (entry.target === viewMoreNext) {
                this.#renderNextPage();
              }
            }
          }
        },
        {
          rootMargin: "100px"
        }
      );
    }

    // Observe the view more elements
    if (viewMorePrevious) {
      this.infinityScrollObserver.observe(viewMorePrevious);
    }

    if (viewMoreNext) {
      this.infinityScrollObserver.observe(viewMoreNext);
    }
  }

  /**
   * @param {{ page: number, url?: URL } | undefined} pageInfo - The page info
   * @returns {boolean} Whether to use the page
   */
  #shouldUsePage(pageInfo) {
    if (!pageInfo) return false;

    const { grid } = this.refs;
    const lastPage = grid?.dataset.lastPage;

    if (!lastPage || pageInfo.page < 1 || pageInfo.page > Number(lastPage))
      return false;

    return true;
  }

  /**
   * @param {"previous" | "next"} type
   */
  async #fetchPage(type) {
    const page = this.#getPage(type);

    // Always resolve the promise, even if we can't fetch the page
    const resolvePromise = () => {
      if (type === "next") {
        this.#resolveNextPagePromise?.();
        this.#resolveNextPagePromise = null;
      } else {
        this.#resolvePreviousPagePromise?.();
        this.#resolvePreviousPagePromise = null;
      }
    };

    if (!page || !this.#shouldUsePage(page)) {
      // Resolve the promise even if we can't fetch
      resolvePromise();
      return;
    }

    await this.#fetchSpecificPage(page.page, page.url);
    resolvePromise();
  }

  /**
   * @param {number} pageNumber - The page number to fetch
   * @param {URL} [url] - Optional URL, will be constructed if not provided
   */
  async #fetchSpecificPage(pageNumber, url = undefined) {
    const pageInfo = { page: pageNumber, url };

    if (!url) {
      const newUrl = new URL(window.location.href);
      newUrl.searchParams.set("page", pageNumber.toString());
      newUrl.hash = "";
      pageInfo.url = newUrl;
    }

    if (!this.#shouldUsePage(pageInfo)) return;
    const pageContent = await sectionRenderer.getSectionHTML(
      this.sectionId,
      true,
      pageInfo.url
    );
    this.pages.set(pageNumber, pageContent);
  }

  /**
   * @param {Iterable<Element>} cards
   */
  #stripReviewsFromJudgeMeBadges(cards) {
    for (const card of cards) {
      if (!(card instanceof HTMLElement)) continue;

      card.querySelectorAll(".jdgm-prev-badge__text").forEach((el) => {
        el.textContent = el.textContent.replace(/reviews/gi, "").trim();
      });
    }
  }

  /**
   * Eye-catcher staggered loading keeps trailing rows of each Shopify page hidden until
   * the next chunk loads. Only unhide the previous Shopify page's rows (not later pages).
   *
   * @param {number} pageNumber - Shopify page (`data-page`) whose deferred rows to show
   */
  #releaseDeferredRowsForPage(pageNumber) {
    const { grid } = this.refs;

    if (
      !(grid instanceof HTMLElement) ||
      grid.dataset.staggeredScrollDefer !== "true"
    )
      return;

    grid
      .querySelectorAll(`[data-deferred-row][data-page="${pageNumber}"]`)
      .forEach((row) => {
        row.removeAttribute("data-deferred-row");
        row.classList.remove("hidden");
      });
  }

  /** @returns {number | undefined} */
  #getLastLoadedPageNumber() {
    const { cards } = this.refs;

    if (!Array.isArray(cards) || cards.length === 0) return;

    const lastCard = cards[cards.length - 1];

    if (!(lastCard instanceof HTMLElement)) return;

    const page = Number(lastCard.dataset.page);

    return Number.isFinite(page) ? page : undefined;
  }

  async #renderNextPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const nextPage = this.#getPage("next");

    if (!nextPage || !this.#shouldUsePage(nextPage)) {
      if (grid.dataset.staggeredScrollDefer === "true") {
        const lastPage = this.#getLastLoadedPageNumber();

        if (lastPage !== undefined) {
          this.#releaseDeferredRowsForPage(lastPage);
        }
        if (this.#isButtonTrigger()) {
          queueMicrotask(() => this.#syncLoadMoreButton());
        }
      }
      return;
    }

    if (grid.dataset.staggeredScrollDefer === "true") {
      this.#releaseDeferredRowsForPage(nextPage.page - 1);
    }

    let nextPageItemElements = this.#getGridForPage(nextPage.page);

    if (!nextPageItemElements) {
      const promise = new Promise((res) => {
        this.#resolveNextPagePromise = res;
      });

      // Trigger the fetch for this page
      this.#fetchPage("next");

      await promise;
      nextPageItemElements = this.#getGridForPage(nextPage.page);
    }

    if (!nextPageItemElements || nextPageItemElements.length === 0) {
      this.pages.delete(nextPage.page);
      if (this.#isButtonTrigger()) {
        queueMicrotask(() => this.#syncLoadMoreButton());
      }
      return;
    }

    const existingIds = new Set(
      [...grid.querySelectorAll(":scope > li[data-product-id]")]
        .map((li) => li.getAttribute("data-product-id"))
        .filter((id) => id != null)
    );

    const nodesToAppend = [...nextPageItemElements].filter((node) => {
      if (!(node instanceof HTMLElement)) return false;

      const id = node.getAttribute("data-product-id");

      if (!id || existingIds.has(id)) return false;

      existingIds.add(id);
      return true;
    });

    if (nodesToAppend.length === 0) {
      this.pages.delete(nextPage.page);

      if (this.#isButtonTrigger()) {
        queueMicrotask(() => this.#syncLoadMoreButton());
      }
      return;
    }

    this.#stripReviewsFromJudgeMeBadges(nodesToAppend);
    grid.append(...nodesToAppend);

    this.#aspectRatioHelper.processNewElements();

    history.pushState("", "", nextPage.url.toString());

    if (this.#isButtonTrigger()) {
      queueMicrotask(() => this.#syncLoadMoreButton());
    }

    requestIdleCallback(() => {
      this.#fetchPage("next");
    });
  }

  async #renderPreviousPage() {
    const { grid } = this.refs;

    if (!grid) return;

    const previousPage = this.#getPage("previous");
    if (!previousPage || !this.#shouldUsePage(previousPage)) return;

    if (this.#renderPreviousInFlight) return;
    this.#renderPreviousInFlight = true;

    try {
      let previousPageItemElements = this.#getGridForPage(previousPage.page);
      if (!previousPageItemElements) {
        const promise = new Promise((res) => {
          this.#resolvePreviousPagePromise = res;
        });

        // Trigger the fetch for this page
        this.#fetchPage("previous");

        await promise;
        previousPageItemElements = this.#getGridForPage(previousPage.page);
      }

      if (!previousPageItemElements || previousPageItemElements.length === 0) {
        this.pages.delete(previousPage.page);
        queueMicrotask(() => {
          this.#syncLoadPreviousButton();
          this.#syncLoadMoreStatus();
        });
        return;
      }

      // Store the current scroll position and height of the first element
      const scrollTop = window.scrollY;
      const firstElement = grid.firstElementChild;
      const oldHeight = firstElement
        ? firstElement.getBoundingClientRect().top + window.scrollY
        : 0;

      this.#stripReviewsFromJudgeMeBadges(previousPageItemElements);
      grid.prepend(...previousPageItemElements);

      this.#aspectRatioHelper.processNewElements();

      history.pushState("", "", previousPage.url.toString());

      // Calculate and adjust scroll position to maintain the same view
      if (firstElement) {
        const newHeight =
          firstElement.getBoundingClientRect().top + window.scrollY;
        const heightDiff = newHeight - oldHeight;
        window.scrollTo({
          top: scrollTop + heightDiff,
          behavior: "instant"
        });
      }

      queueMicrotask(() => {
        this.#syncLoadPreviousButton();
        this.#syncLoadMoreStatus();
      });

      requestIdleCallback(() => {
        this.#fetchPage("previous");
      });
    } finally {
      this.#renderPreviousInFlight = false;
    }
  }

  /**
   * @param {"previous" | "next"} type
   * @returns {{ page: number, url: URL } | undefined}
   */
  #getPage(type) {
    const { cards } = this.refs;
    const isPrevious = type === "previous";

    if (!Array.isArray(cards)) return;

    const targetCard = cards[isPrevious ? 0 : cards.length - 1];

    if (!targetCard) return;

    const currentCardPage = Number(targetCard.dataset.page);
    const page = isPrevious ? currentCardPage - 1 : currentCardPage + 1;

    const url = new URL(window.location.href);
    url.searchParams.set("page", page.toString());
    url.hash = "";

    return {
      page,
      url
    };
  }

  /**
   * @param {number} page
   * @returns {NodeListOf<Element> | undefined}
   */
  #getGridForPage(page) {
    const pageHTML = this.pages.get(page);

    if (!pageHTML) return;

    const parsedPage = new DOMParser().parseFromString(pageHTML, "text/html");
    const gridElement = parsedPage.querySelector('[ref="grid"]');
    if (!gridElement) return;
    return gridElement.querySelectorAll(':scope > [ref="cards[]"]');
  }

  /**
   * Shows or hides the load-more footer: compare the last grid row's `data-page`
   * to `data-last-page` on the list (from Liquid `paginate.pages`).
   * Uses the global `.hidden` class (`base.css`, `display: none !important`) because
   * `.product-grid__load-more { display: flex }` overrides the native `[hidden]` attribute.
   */
  #syncLoadMoreButton() {
    const footer = this.refs.loadMoreFooter;
    const { grid } = this.refs;
    const { cards } = this.refs;

    if (!(footer instanceof HTMLElement) || !grid) return;

    const lastPage = Number(grid.dataset.lastPage);
    if (!Number.isFinite(lastPage) || lastPage < 1) {
      footer.classList.add("hidden");
      this.#syncLoadMoreStatus();
      return;
    }

    if (!Array.isArray(cards) || cards.length === 0) {
      footer.classList.add("hidden");
      this.#syncLoadMoreStatus();
      return;
    }

    const lastCard = cards[cards.length - 1];
    if (!(lastCard instanceof HTMLElement)) {
      footer.classList.add("hidden");
      this.#syncLoadMoreStatus();
      return;
    }

    const lastLoadedPage = Number(lastCard.dataset.page);
    if (!Number.isFinite(lastLoadedPage)) {
      footer.classList.add("hidden");
      this.#syncLoadMoreStatus();
      return;
    }

    const hasDeferred =
      grid.dataset.staggeredScrollDefer === "true" &&
      Boolean(grid.querySelector("[data-deferred-row]"));

    footer.classList.toggle(
      "hidden",
      lastLoadedPage >= lastPage && !hasDeferred
    );
    this.#syncLoadMoreStatus();
  }

  /**
   * Updates the “showing start–end of total” line under the load-more button (button mode).
   * Range uses min/max `data-collection-index` on product rows (1-based in filtered set); falls back to 1…loaded
   * when the attribute is missing. Template and total come from Liquid (`data-range-template`, `data-total-paginate-items`).
   */
  #syncLoadMoreStatus() {
    if (!this.#isButtonTrigger()) return;

    const status = this.refs.loadMoreStatus;
    const footer = this.refs.loadMoreFooter;
    const { grid } = this.refs;

    if (
      !(status instanceof HTMLElement) ||
      !(footer instanceof HTMLElement) ||
      !grid
    )
      return;

    const template = footer.dataset.rangeTemplate;
    if (!template) return;

    const total = Number(grid.dataset.totalPaginateItems);
    const productRows = grid.querySelectorAll(":scope > li[data-product-id]");
    const loaded = productRows.length;

    if (!Number.isFinite(total) || total < 1 || loaded < 1) {
      status.textContent = "";
      return;
    }

    /** @type {number[]} */
    const indices = [];
    for (const li of productRows) {
      if (!(li instanceof HTMLElement)) continue;
      const n = Number(li.dataset.collectionIndex);
      if (Number.isFinite(n) && n >= 1) indices.push(n);
    }

    let start = 1;
    let end = Math.min(loaded, total);
    if (indices.length > 0) {
      start = Math.min(...indices);
      end = Math.min(Math.max(...indices), total);
    }

    status.textContent = template
      .split("__S__")
      .join(String(start))
      .split("__E__")
      .join(String(end))
      .split("__T__")
      .join(String(total));
  }

  /**
   * Hides the load-previous control when the first row is already page 1 (or invalid).
   * Uses `.hidden` for the same reason as {@link #syncLoadMoreButton}.
   */
  #syncLoadPreviousButton() {
    const footer = this.refs.loadPreviousFooter;
    if (!(footer instanceof HTMLElement)) return;

    const { cards } = this.refs;
    if (!Array.isArray(cards) || cards.length === 0) {
      footer.classList.add("hidden");
      return;
    }

    const firstCard = cards[0];
    if (!(firstCard instanceof HTMLElement)) {
      footer.classList.add("hidden");
      return;
    }

    const firstLoadedPage = Number(firstCard.dataset.page);
    if (!Number.isFinite(firstLoadedPage)) {
      footer.classList.add("hidden");
      return;
    }

    footer.classList.toggle("hidden", firstLoadedPage <= 1);
  }

  /**
   * Prepends one earlier page (manual control for deep-linked `?page=N` and parity with scroll sentinels).
   */
  loadPreviousPage = async () => {
    if (!this.#appendPaginationEnabled()) return;

    const previousPage = this.#getPage("previous");
    if (!previousPage || !this.#shouldUsePage(previousPage)) {
      this.#syncLoadPreviousButton();
      return;
    }

    const btn = this.refs.loadPreviousButton;
    if (btn instanceof HTMLButtonElement) {
      btn.disabled = true;
      btn.setAttribute("aria-busy", "true");
    }

    try {
      await this.#renderPreviousPage();
    } finally {
      const btnAfter = this.refs.loadPreviousButton;
      if (btnAfter instanceof HTMLButtonElement && btnAfter.isConnected) {
        btnAfter.removeAttribute("aria-busy");
        btnAfter.disabled = false;
      }
      this.#syncLoadPreviousButton();
      this.#syncLoadMoreStatus();
    }
  };

  /**
   * Appends the next page when using load-more button mode (declarative `on:click`).
   */
  loadMoreNext = async () => {
    if (!this.#isButtonTrigger() || this.#loadMoreInFlight) return;

    const nextPage = this.#getPage("next");
    if (!nextPage || !this.#shouldUsePage(nextPage)) {
      this.#syncLoadMoreButton();
      return;
    }

    const btn = this.refs.loadMoreButton;
    this.#loadMoreInFlight = true;
    if (btn instanceof HTMLButtonElement) {
      btn.disabled = true;
      btn.setAttribute("aria-busy", "true");
    }

    try {
      await this.#renderNextPage();
    } finally {
      this.#loadMoreInFlight = false;
      const btnAfter = this.refs.loadMoreButton;
      if (btnAfter instanceof HTMLButtonElement && btnAfter.isConnected) {
        btnAfter.removeAttribute("aria-busy");
        btnAfter.disabled = false;
      }
      this.#syncLoadMoreButton();
    }
  };

  get sectionId() {
    const id = this.getAttribute("section-id");

    if (!id) throw new Error("The section-id attribute is required");

    return id;
  }

  /**
   * Handle filter updates by clearing cached pages
   */
  #handleFilterUpdate = () => {
    this.pages.clear();

    // Resolve any pending promises to unblock waiting renders
    this.#resolveNextPagePromise?.();
    this.#resolvePreviousPagePromise?.();

    this.#resolveNextPagePromise = null;
    this.#resolvePreviousPagePromise = null;

    // Store the current lastPage and total count to detect when the grid updates after filtering
    const currentLastPage = this.refs.grid?.dataset.lastPage;
    const currentTotal = this.refs.grid?.dataset.totalPaginateItems;

    // We need to wait for the DOM to be updated with the new filtered content
    // Using mutation observer to detect when the grid actually updates
    const observer = new MutationObserver(() => {
      const newLastPage = this.refs.grid?.dataset.lastPage;
      const newTotal = this.refs.grid?.dataset.totalPaginateItems;

      if (newLastPage !== currentLastPage || newTotal !== currentTotal) {
        observer.disconnect();

        // Check if component is still connected
        if (!this.isConnected) {
          return;
        }

        // Now the DOM has been updated with the new filtered content
        if (this.#isScrollTrigger()) {
          this.#observeViewMore();
        } else if (this.#isButtonTrigger()) {
          this.#syncLoadMoreButton();
        }

        queueMicrotask(() => {
          this.#syncLoadPreviousButton();
          this.#syncLoadMoreStatus();
        });

        // Fetch the next page
        if (this.#appendPaginationEnabled()) {
          this.#fetchPage("next");
        }
      }
    });

    // Observe the grid for changes
    const { grid } = this.refs;
    if (grid) {
      observer.observe(grid, {
        attributes: true,
        attributeFilter: ["data-last-page", "data-total-paginate-items"],
        childList: true // Also watch for child changes in case the whole grid is replaced
      });

      // Set a timeout as a fallback in case the mutation never fires
      setTimeout(() => {
        if (observer) {
          observer.disconnect();
        }
      }, 3000);
    }
  };
}
